/* global MediaQueryListEvent */
// D39: Dark mode theme management — device-local, not synced.
// Stores preference in localStorage; applies via <html class="dark">.
// Safe for SSR/test environments — all DOM/localStorage access is guarded.

export type ThemeMode = "light" | "dark" | "system";

const THEME_KEY = "personal-life-os:theme";

// ── Persistence ───────────────────────────────────────────────────────────────

/**
 * Read stored theme preference.
 * Falls back to "light" on any error or if unset.
 */
export function getThemeMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw === "dark" || raw === "system") return raw;
    return "light";
  } catch {
    return "light";
  }
}

/**
 * Persist theme preference to localStorage.
 * Silently swallows quota / private-browsing errors.
 */
export function setThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_KEY, mode);
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ── Resolution ────────────────────────────────────────────────────────────────

/**
 * Resolve "system" → actual "light" | "dark" based on OS preference.
 * Safe in environments without matchMedia (returns "light").
 */
export function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  // "system"
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

// ── DOM application ───────────────────────────────────────────────────────────

/**
 * Apply the given mode to the document root immediately.
 * Toggles `class="dark"` on <html>. Safe to call before React mounts.
 */
export function applyTheme(mode: ThemeMode): void {
  try {
    const resolved = resolveTheme(mode);
    if (resolved === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    // Keep native controls (selects, date pickers, scrollbars) in sync with the theme.
    document.documentElement.style.colorScheme = resolved;
  } catch {
    // Non-browser environment (tests without jsdom) — skip.
  }
}

/**
 * Apply the UI theme layout.
 * Toggles "theme-classic" vs "theme-aura" classes on documentElement.
 */
export function applyUiTheme(theme: "classic" | "aura"): void {
  try {
    if (theme === "aura") {
      document.documentElement.classList.add("theme-aura");
      document.documentElement.classList.remove("theme-classic");
    } else {
      document.documentElement.classList.add("theme-classic");
      document.documentElement.classList.remove("theme-aura");
    }
  } catch {
    // Non-browser environment — skip.
  }
}


// ── System preference watcher ─────────────────────────────────────────────────

/**
 * Subscribe to OS color-scheme changes.
 * Only fires when the stored mode is "system" so manual picks are unaffected.
 * Returns a cleanup function — call it to unsubscribe.
 */
export function watchSystemTheme(callback: (resolved: "light" | "dark") => void): () => void {
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => callback(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  } catch {
    return () => {};
  }
}
